/* eslint-disable */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { NotificationsNoneOutlined } from '@mui/icons-material'
import { Trash2, X } from 'lucide-react'
import { useRecoilRefresher_UNSTABLE, useRecoilValueLoadable } from 'recoil'
import { patch } from '../http/restservice'
import { NotificationsSelector } from '../store/AccountDetailsStore'
import {
  formatRelativeNotificationTime,
  getParsedNotificationItems,
  isNotificationNewerThanOneDay,
  isNotificationNewerThanOneWeek,
} from '../utils/notificationUtils'
import { useThemeMode } from '../Provider/ThemeContextProvider'
import { useNavigate } from 'react-router-dom'

const NOTIFICATION_TABS = [
  { id: 'forYou', label: 'For you' },
  { id: 'market', label: 'Markets', emptyMessage: 'No new market updates' },
]

const TopbarNotifications = ({ className = '', variant = 'desktop', onOpenChange }) => {
  const navigate = useNavigate()
  const { state, contents: notifications } = useRecoilValueLoadable(NotificationsSelector)
  const refreshNotifications = useRecoilRefresher_UNSTABLE(NotificationsSelector)
  const { darkMode } = useThemeMode()
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [notificationItems, setNotificationItems] = useState([])
  const [activeNotificationTab, setActiveNotificationTab] = useState('forYou')
  const [now, setNow] = useState(Date.now())
  const [panelStyle, setPanelStyle] = useState({})
  const [hasHiddenNotificationsBelow, setHasHiddenNotificationsBelow] = useState(false)
  const [expandedNotificationId, setExpandedNotificationId] = useState(null)
  const [isTabSwitching, setIsTabSwitching] = useState(false)
  const rootRef = useRef(null)
  const notificationListRef = useRef(null)
  const closeTimeoutRef = useRef(null)
  const tabSwitchTimeoutRef = useRef(null)
  const suppressOutsideClickRef = useRef(false)

  useEffect(() => {
    if (state === 'hasValue') {
      setNotificationItems(getParsedNotificationItems(notifications, { onlyUnread: true }))
    }

    if (state === 'hasError') {
      setNotificationItems([])
    }
  }, [state, notifications])

  const hasUnreadNotifications = useMemo(
    () => notificationItems.some((notification) => notification.isUnread),
    [notificationItems],
  )
  const hasNotifications = notificationItems.length > 0
  const activeNotifications = useMemo(
    () =>
      notificationItems.filter(
        (notification) => notification.notificationCategory === activeNotificationTab,
      ),
    [activeNotificationTab, notificationItems],
  )

  useEffect(() => {
    onOpenChange?.(isOpen)
  }, [isOpen, onOpenChange])

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
      if (tabSwitchTimeoutRef.current) clearTimeout(tabSwitchTimeoutRef.current)
    },
    [],
  )

  useEffect(() => {
    if (!isOpen) return undefined

    setNow(Date.now())
    const intervalId = window.setInterval(() => setNow(Date.now()), 60000)

    return () => window.clearInterval(intervalId)
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen) return undefined

    const syncPanelStyle = () => {
      const topbar = rootRef.current?.closest('.fabits-mobile-topbar, .fabits-desktop-topbar')
      if (!topbar) return

      const topbarRect = topbar.getBoundingClientRect()

      setPanelStyle({
        '--fabits-topbar-notification-top': `${Math.round(topbarRect.bottom)}px`,
      })
    }

    syncPanelStyle()
    window.addEventListener('resize', syncPanelStyle)

    return () => window.removeEventListener('resize', syncPanelStyle)
  }, [darkMode, isOpen, variant])

  useLayoutEffect(() => {
    if (!isOpen) {
      setHasHiddenNotificationsBelow(false)
      return undefined
    }

    const list = notificationListRef.current
    if (!list) {
      setHasHiddenNotificationsBelow(false)
      return undefined
    }

    const syncListOverflow = () => {
      const hiddenBelow = list.scrollHeight - list.scrollTop - list.clientHeight > 2
      setHasHiddenNotificationsBelow(hiddenBelow)
    }

    syncListOverflow()
    list.addEventListener('scroll', syncListOverflow)
    window.addEventListener('resize', syncListOverflow)

    return () => {
      list.removeEventListener('scroll', syncListOverflow)
      window.removeEventListener('resize', syncListOverflow)
    }
  }, [activeNotificationTab, activeNotifications.length, expandedNotificationId, isOpen])

  const openPanel = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = null
    setIsClosing(false)
    setNow(Date.now())
    setIsOpen(true)
  }

  const closePanel = () => {
    if (!isOpen || isClosing) return

    setIsClosing(true)
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
      closeTimeoutRef.current = null
    }, 210)
  }

  const consumeDismissEvent = (event) => {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation?.()
    event.nativeEvent?.stopImmediatePropagation?.()
  }

  const handleBackdropDismiss = (event) => {
    consumeDismissEvent(event)
    closePanel()
  }

  useEffect(() => {
    if (!isOpen) return undefined

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return

      suppressOutsideClickRef.current = true
      consumeDismissEvent(event)
      closePanel()
    }

    const handleClick = (event) => {
      const shouldSuppress =
        suppressOutsideClickRef.current || !rootRef.current?.contains(event.target)

      if (!shouldSuppress) return

      suppressOutsideClickRef.current = false
      consumeDismissEvent(event)
      closePanel()
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('click', handleClick, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('click', handleClick, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (!hasNotifications && isOpen && !isClosing) closePanel()
  }, [hasNotifications, isClosing, isOpen])

  const markNotificationsAsRead = async (notificationIds) => {
    const ids = notificationIds.filter(Boolean)

    if (!ids.length) return 0

    const responses = await Promise.all(
      ids.map((notificationId) =>
        patch(`customerservice/api/notification/${notificationId}/markAsRead`),
      ),
    )
    const successfulIds = ids.filter((_, index) => !responses[index]?.isError)

    if (successfulIds.length) {
      setNotificationItems((prevState) =>
        prevState.filter((notification) => !successfulIds.includes(notification.notificationId)),
      )
      refreshNotifications()
    }

    return successfulIds.length
  }

  const getReadEligibleNotificationIds = (items) =>
    items
      .filter((notification) =>
        notification.notificationCategory === 'market'
          ? !isNotificationNewerThanOneDay(notification.timestamp, now)
          : !isNotificationNewerThanOneWeek(notification.timestamp, now),
      )
      .map((notification) => notification.notificationId)
      .filter(Boolean)

  const handleBellClick = async () => {
    if (isOpen && !isClosing) {
      if (activeNotifications.length) {
        await markNotificationsAsRead(getReadEligibleNotificationIds(activeNotifications))
      }
      closePanel()
      return
    }

    openPanel()
  }

  const handleNotificationTabChange = (tabId) => {
    if (tabId === activeNotificationTab || isTabSwitching) return

    setExpandedNotificationId(null)
    setIsTabSwitching(true)
    tabSwitchTimeoutRef.current = setTimeout(() => {
      setActiveNotificationTab(tabId)
      requestAnimationFrame(() => setIsTabSwitching(false))
    }, 180)
  }

  const handleNotificationClick = async (notification) => {
    const target = notification.deeplink ||
      (notification.action.toUpperCase() === 'DEEP_LINK' ? notification.route : '')
    let shouldNavigate = false

    if (target) {
      try {
        const targetUrl = new URL(target, window.location.origin)
        const isExternal =
          ['http:', 'https:'].includes(targetUrl.protocol) &&
          targetUrl.origin !== window.location.origin

        if (isExternal) {
          window.open(targetUrl.href, '_blank', 'noopener,noreferrer')
        } else if (targetUrl.origin === window.location.origin) {
          navigate(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`)
        }
        shouldNavigate = ['http:', 'https:'].includes(targetUrl.protocol)
      } catch (error) {
        // Ignore malformed deeplinks and continue marking the notification as read.
      }
    }

    const shouldMarkAsRead =
      notification.notificationCategory === 'market'
        ? !isNotificationNewerThanOneDay(notification.timestamp, now)
        : !isNotificationNewerThanOneWeek(notification.timestamp, now)
    const markedCount = shouldMarkAsRead
      ? await markNotificationsAsRead([notification.notificationId])
      : 0
    if (shouldNavigate || (markedCount && notificationItems.length <= 1)) closePanel()
  }

  const handleClearAllClick = async () => {
    if (!activeNotifications.length) return

    const notificationIds = activeNotifications
      .map((notification) => notification.notificationId)
      .filter(Boolean)

    if (!notificationIds.length) {
      setNotificationItems((prevState) =>
        prevState.filter(
          (notification) => notification.notificationCategory !== activeNotificationTab,
        ),
      )
      closePanel()
      return
    }

    const markedCount = await markNotificationsAsRead(notificationIds)

    if (markedCount >= notificationIds.length) {
      closePanel()
    }
  }

  if (!hasNotifications && !isOpen) return null

  return (
    <div className={`fabits-topbar-notifications ${className}`} ref={rootRef}>
      <button
        type='button'
        className={`fabits-topbar-notification-button ${
          hasUnreadNotifications ? 'is-unread' : 'is-read'
        }`}
        aria-label={hasUnreadNotifications ? 'Unread notifications' : 'Notifications'}
        aria-expanded={isOpen && !isClosing}
        data-testid='topbar-notification-bell'
        onClick={handleBellClick}
      >
        <NotificationsNoneOutlined className='fabits-topbar-notification-icon' />
        {hasUnreadNotifications && <span className='fabits-topbar-notification-dot' />}
      </button>

      {isOpen && (
        <>
          <div
            className={`fabits-topbar-notification-backdrop ${isClosing ? 'is-closing' : ''}`}
            aria-hidden='true'
            style={panelStyle}
            onPointerDown={handleBackdropDismiss}
            onClick={handleBackdropDismiss}
          />
          <div
            className={`fabits-topbar-notification-panel is-${variant} ${
              isClosing ? 'is-closing' : ''
            }`}
            data-testid='topbar-notification-panel'
            aria-live='polite'
            style={panelStyle}
          >
            <div className='fabits-topbar-notification-panel-inner'>
              <div className='fabits-topbar-notification-panel-header'>
                <div
                  className={`fabits-topbar-notification-tabs ${
                    activeNotificationTab === 'market' ? 'is-market-active' : ''
                  }`}
                  role='tablist'
                >
                  {NOTIFICATION_TABS.map((tab) => {
                    const isActive = activeNotificationTab === tab.id

                    return (
                      <button
                        type='button'
                        key={tab.id}
                        className={`fabits-topbar-notification-tab ${isActive ? 'is-active' : ''}`}
                        role='tab'
                        aria-selected={isActive}
                        onClick={() => handleNotificationTabChange(tab.id)}
                      >
                        {tab.label}
                      </button>
                    )
                  })}
                </div>
                <div className='fabits-topbar-notification-actions'>
                  {activeNotifications.length ? (
                    <button
                      type='button'
                      className='fabits-topbar-notification-clear-all'
                      aria-label='Clear all notifications'
                      onClick={handleClearAllClick}
                    >
                      <Trash2 aria-hidden='true' strokeWidth={2} />
                    </button>
                  ) : null}
                  <button
                    type='button'
                    className='fabits-topbar-notification-close'
                    aria-label='Close notifications'
                    onClick={closePanel}
                  >
                    <X aria-hidden='true' strokeWidth={2} />
                  </button>
                </div>
              </div>
              {hasNotifications ? (
                <>
                  {activeNotifications.length ? (
                    <div
                      key={activeNotificationTab}
                      className={`fabits-topbar-notification-list-shell ${
                        hasHiddenNotificationsBelow ? 'has-hidden-below' : ''
                      } ${expandedNotificationId ? 'is-expanded' : ''} ${
                        isTabSwitching ? 'is-switching' : ''
                      }`}
                    >
                      <div className='fabits-topbar-notification-list' ref={notificationListRef}>
                        {activeNotifications.map((notification) => {
                          const relativeTime = formatRelativeNotificationTime(
                            notification.timestamp,
                            now,
                          )

                          const isImageExpanded = expandedNotificationId === notification.id

                          return (
                            <div
                              key={notification.id}
                              className={`fabits-topbar-notification-item ${
                                isImageExpanded ? 'is-image-expanded' : ''
                              }`}
                            >
                              {notification.imageUrl ? (
                                <button
                                  type='button'
                                  className='fabits-topbar-notification-image-button'
                                  aria-expanded={isImageExpanded}
                                  aria-label={`${
                                    isImageExpanded ? 'Collapse' : 'Expand'
                                  } ${notification.title || 'notification'} image`}
                                  onClick={() =>
                                    setExpandedNotificationId((currentId) =>
                                      currentId === notification.id ? null : notification.id,
                                    )
                                  }
                                >
                                  <img
                                    className='fabits-topbar-notification-image'
                                    src={notification.imageUrl}
                                    alt=''
                                    loading='lazy'
                                  />
                                </button>
                              ) : null}
                              <button
                                type='button'
                                className='fabits-topbar-notification-content'
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <span className='fabits-topbar-notification-item-top'>
                                  <span className='fabits-topbar-notification-title'>
                                    {notification.title || 'Notification'}
                                  </span>
                                  {relativeTime ? (
                                    <span className='fabits-topbar-notification-time'>
                                      {relativeTime}
                                    </span>
                                  ) : null}
                                </span>
                                <span className='fabits-topbar-notification-description'>
                                  {notification.description || notification.text}
                                </span>
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className='fabits-topbar-notification-empty'>
                      {NOTIFICATION_TABS.find((tab) => tab.id === activeNotificationTab)
                        ?.emptyMessage ||
                        `No ${
                          NOTIFICATION_TABS.find((tab) => tab.id === activeNotificationTab)?.label
                        } notifications`}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </>
      )}

    </div>
  )
}

export default TopbarNotifications
